import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FolderClosed, FileText, HelpCircle, Link2, FileType2, Image, FileSpreadsheet, MoreHorizontal } from "lucide-react";
import { User, displayName, initials, avatarColor } from "./user-display";
import { LucideIcon } from "./icon-registry";

export type RowKind = "folder" | "page" | "question" | "link" | "document";

const EXT_IMAGE = new Set(["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "heic"]);
const EXT_PDF = new Set(["pdf"]);
const EXT_DOC = new Set(["doc", "docx", "rtf", "odt"]);
const EXT_SHEET = new Set(["xls", "xlsx", "csv", "ods"]);
const EXT_PRES = new Set(["ppt", "pptx", "key", "odp"]);
const EXT_TEXT = new Set(["txt", "md", "log"]);

function extOf(filename: string | undefined | null): string {
    if (!filename) return "";
    const dot = filename.lastIndexOf(".");
    if (dot <= 0) return "";
    return filename.slice(dot + 1).toLowerCase();
}

function documentIcon(filename: string | undefined | null) {
    const e = extOf(filename);
    if (EXT_IMAGE.has(e)) return <Image size={18} className="icon" color="#10b981" />;
    if (EXT_PDF.has(e)) return <FileText size={18} className="icon" color="#ef4444" />;
    if (EXT_DOC.has(e)) return <FileText size={18} className="icon" color="#1e85e5" />;
    if (EXT_SHEET.has(e)) return <FileSpreadsheet size={18} className="icon" color="#10b981" />;
    if (EXT_PRES.has(e)) return <FileType2 size={18} className="icon" color="#f59e0b" />;
    if (EXT_TEXT.has(e)) return <FileText size={18} className="icon" color="#64748b" />;
    return <FileType2 size={18} className="icon" color="#64748b" />;
}

export function RowIcon({ kind, filename, iconName, iconColor }: { kind: RowKind; filename?: string | null; iconName?: string | null; iconColor?: string | null }) {
    switch (kind) {
        case "folder": return (
            <span className="m-row-folder-swatch" style={{ backgroundColor: iconColor || "#94a3b8" }}>
                <LucideIcon name={iconName || "FolderClosed"} size={14} color="#fff" />
            </span>
        );
        case "page": return <FileText size={18} className="icon" color="#1e85e5" />;
        case "question": return <HelpCircle size={18} className="icon" color="#10b981" />;
        case "link": return <Link2 size={18} className="icon" color="#1e85e5" />;
        case "document": return documentIcon(filename);
    }
}

export function OwnerCell({ userId, users }: { userId: string | null; users: Map<string, User> }) {
    if (!userId) return <span>—</span>;
    const u = users.get(userId);
    if (!u) {
        const short = userId.slice(0, 8);
        return (
            <>
                <span className="m-comment-avatar" style={{ background: avatarColor(userId) }}>
                    {short.slice(0, 2).toUpperCase()}
                </span>
                <span>{short}</span>
            </>
        );
    }
    const name = displayName(u);
    return (
        <>
            <span className="m-comment-avatar" style={{ background: avatarColor(u.id) }}>
                {initials(name)}
            </span>
            <span>{name}</span>
        </>
    );
}

export function formatRelative(iso: string): string {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const days = Math.floor(diff / 86400000);
    if (days < 1) return "today";
    if (days === 1) return "yesterday";
    if (days < 30) return `${days} days ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return `${Math.floor(days / 365)} years ago`;
}

export interface KebabItem {
    label: string;
    onClick: () => void;
    danger?: boolean;
}

export function RowKebab({ isOpen, onToggle, items }: {
    isOpen: boolean;
    onToggle: () => void;
    items: KebabItem[];
}) {
    const btnRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);

    useLayoutEffect(() => {
        if (!isOpen || !btnRef.current) return;
        const update = () => {
            if (!btnRef.current) return;
            const r = btnRef.current.getBoundingClientRect();
            setMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
        };
        update();
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const onClick = (e: MouseEvent) => {
            const t = e.target as Node;
            if (btnRef.current?.contains(t)) return;
            if (menuRef.current?.contains(t)) return;
            onToggle();
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [isOpen, onToggle]);

    return (
        <>
            <button
                ref={btnRef}
                className="m-cat-row-kebab-btn"
                title="More actions"
                onClick={e => { e.stopPropagation(); onToggle(); }}
            >
                <MoreHorizontal size={15} />
            </button>
            {isOpen && menuPos && createPortal(
                <div
                    ref={menuRef}
                    className="m-cat-kebab-menu"
                    style={{ position: "fixed", top: menuPos.top, right: menuPos.right, zIndex: 1080 }}
                >
                    {items.map(item => (
                        <button
                            key={item.label}
                            className={item.danger ? "m-cat-kebab-danger" : undefined}
                            onClick={e => { e.stopPropagation(); item.onClick(); }}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </>
    );
}
